import { useEffect, useState } from "react";

import { api, type Published, type SpreadRow } from "../api";

/**
 * Feed curation. Two layers, both instant:
 *  - curated order: the approved spreads' order on the CDN (republishes)
 *  - overrides: pinned / hidden, applied by the API at feed time
 */
export function Feed() {
  const [rows, setRows] = useState<SpreadRow[]>([]);
  const [order, setOrder] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<{ pinned: string[]; hidden: string[] }>({ pinned: [], hidden: [] });
  const [catalog, setCatalog] = useState<Array<{ id: string; order: number }>>([]);
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    api<{ spreads: SpreadRow[]; curated: string[] }>("/admin/spreads").then((r) => {
      setRows(r.spreads);
      // Every spread appears in the order list: approved ones publish in this order, the rest wait their turn.
      const ids = r.spreads.map((s) => s.id);
      setOrder([...r.curated.filter((id) => ids.includes(id)), ...ids.filter((id) => !r.curated.includes(id))]);
    }),
    api<{ overrides: { pinned: string[]; hidden: string[] } }>("/admin/overview").then((r) => setOverrides(r.overrides)),
    api<{ catalog: Array<{ id: string; order: number }> }>("/admin/catalog").then((r) => setCatalog(r.catalog)),
  ]).catch((e: Error) => setMsg({ ok: false, text: e.message }));
  useEffect(() => { void load(); }, []);

  const move = (id: string, dir: -1 | 1) => {
    const i = order.indexOf(id); const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    const next = [...order]; [next[i], next[j]] = [next[j]!, next[i]!]; setOrder(next);
  };
  const saveOrder = async () => {
    setBusy(true); setMsg(undefined);
    try { const r = await api<{ published: Published }>("/admin/curation", { method: "PUT", body: { order } }); setMsg({ ok: true, text: `Order saved; ${r.published.published} chains republished.` }); await load(); }
    catch (e) { setMsg({ ok: false, text: (e as Error).message }); } finally { setBusy(false); }
  };
  const republish = async () => {
    setBusy(true); setMsg(undefined);
    try { const r = await api<Published>("/admin/publish", { method: "POST" }); setMsg({ ok: true, text: `Republished ${r.published} chains, removed ${r.removed} stale.` }); await load(); }
    catch (e) { setMsg({ ok: false, text: (e as Error).message }); } finally { setBusy(false); }
  };
  const toggle = async (list: "pinned" | "hidden", id: string) => {
    const cur = overrides[list];
    const next = { ...overrides, [list]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
    setOverrides(next);
    try { await api("/admin/feed", { method: "PUT", body: next }); } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  };

  const byId = new Map(rows.map((r) => [r.id, r]));
  const published = new Set(catalog.map((c) => c.id));

  return (
    <>
      <h2>Feed</h2>
      <p className="sub">Guests see the curated order. Signed-in readers get their topics boosted and unseen first. Pinned always lead; hidden never show. Ranking is one function — <code>backend/src/lib/feed.ts</code>.</p>
      {msg && <div className={`msg ${msg.ok ? "ok" : "err"}`}>{msg.text}</div>}
      <div className="split">
        <div>
          <div className="head">Curated order</div>
          <ul className="orderlist" style={{ marginTop: 8 }}>
            {order.map((id, i) => {
              const r = byId.get(id);
              return (
                <li key={id}>
                  <span className="chrome" style={{ width: 22 }}>{i + 1}</span>
                  <span className="sp">{r?.title || id} <span className={`pill ${r?.status ?? "draft"}`}>{r?.status ?? "?"}</span>{published.has(id) ? "" : <span className="chrome"> · not on CDN</span>}</span>
                  <button className="btn" onClick={() => move(id, -1)} disabled={i === 0}>↑</button>
                  <button className="btn" onClick={() => move(id, 1)} disabled={i === order.length - 1}>↓</button>
                </li>
              );
            })}
          </ul>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="btn primary" disabled={busy || order.length === 0} onClick={saveOrder}>Save order</button>
            <button className="btn" disabled={busy} onClick={republish}>Republish</button>
          </div>
          <p className="chrome" style={{ marginTop: 8 }}>{published.size} on the CDN now.</p>
        </div>
        <div>
          <div className="head">Overrides · instant</div>
          <table className="tbl" style={{ marginTop: 8 }}>
            <thead><tr><th>Chain</th><th>Pinned</th><th>Hidden</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.title || r.id}<br /><span className="chrome">{r.id}</span></td>
                  <td><input type="checkbox" checked={overrides.pinned.includes(r.id)} onChange={() => toggle("pinned", r.id)} /></td>
                  <td><input type="checkbox" checked={overrides.hidden.includes(r.id)} onChange={() => toggle("hidden", r.id)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
